import { encode } from '@src/shared/utils/qs'
import { getCookie, setCookie } from '@src/shared/utils/cookie'
import { getForterToken } from '@src/shared/utils/forter'
import { FORTER_HEADER_NAME } from '@src/shared/constants'
import { selectUnverifiedPhone, selectMfaUserCreds } from '@store/user/reducer'
import { selectApplicantCustomerId, selectEmailMatch } from '@store/applicant/reducer'
import {
  AUTH_TOKEN,
  SIGN_IN_SUCCESS,
  SIGN_IN_REQUEST,
  CUSTOMER_SUCCESS,
  CUSTOMER_REQUEST,
  CUSTOMER_FORM_ERROR,
  CLEAR_CUSTOMER_ERROR,
  USER_DATA_SUCCESS,
  REQUEST_MFA_CODE_REQUEST,
  REQUEST_MFA_CODE_SUCCESS,
  REQUEST_MFA_CODE_FAILURE,
  SET_USER_CREDS,
  DELETE_USER_CREDS,
  CUSTOMER_RESPONSE_ERROR
} from '@store/constants'
import { handleError, stateRequest } from '@store/utils'
import request from '@store/request'
import { resubmitApply, fetchApplicant, fetchApps } from '@store/applicant/actions'

export const fetchCustomer = () => async (dispatch, getState) => {
  const { token, coreApi, accountId } = stateRequest(getState())
  const url = `${coreApi}/api/v6/customers/${accountId}`

  try {
    dispatch({
      type: CUSTOMER_REQUEST
    })
    const customer = await request(token)(url)

    dispatch({
      type: CUSTOMER_SUCCESS,
      data: customer.data.customer
    })
  } catch (err) {
    dispatch(handleCustomerError(err))
  }
}

export const signIn =
  (email, password, mfaCode = undefined) =>
  async (dispatch, getState) => {
    try {
      const state = getState()
      const url = `${state.sourceRequest.coreApi}/api/v6/auth/login`

      const forterToken = getForterToken('sign in')

      dispatch({
        type: SIGN_IN_REQUEST
      })

      const res = await request()(
        url,
        'post',
        encode({
          email,
          password,
          ...(mfaCode ? { mfa_code: mfaCode } : {})
        }),
        { [FORTER_HEADER_NAME]: forterToken }
      )
      const token = res.data.jwt

      // resubmitting signup form to update data correctly if email and phone didn't match previously
      if (!selectEmailMatch(state)) {
        dispatch(resubmitApply(email))
      }

      setCookie(AUTH_TOKEN, token)
      dispatch({
        type: SIGN_IN_SUCCESS,
        token
      })
    } catch (err) {
      if (handleError(err).warnings?.some(w => w.id === 'login-mfa-required')) {
        dispatch({
          type: SET_USER_CREDS,
          userCreds: { email, password }
        })
      }

      dispatch(handleCustomerError(err))
      return
    }

    const { accountId } = stateRequest(getState())
    const applicantCustomerId = selectApplicantCustomerId(getState())
    const requests = [dispatch(fetchApps()), dispatch(fetchCustomer())]
    if (accountId !== applicantCustomerId) {
      requests.push(dispatch(fetchApplicant()))
    }

    await Promise.all(requests)

    dispatch({
      type: USER_DATA_SUCCESS,
      accountId
    })
  }

// Restore session from token cookie on load.
export const hydrateTokenFromCookie = () => async (dispatch, getState) => {
  const token = getCookie(AUTH_TOKEN)

  if (!token) {
    return
  }

  dispatch({ type: SIGN_IN_SUCCESS, token })

  const { accountId } = stateRequest(getState())
  const applicantCustomerId = selectApplicantCustomerId(getState())
  const requests = [dispatch(fetchApps()), dispatch(fetchCustomer())]
  if (accountId !== applicantCustomerId) {
    requests.push(dispatch(fetchApplicant()))
  }

  await Promise.all(requests)

  dispatch({ type: USER_DATA_SUCCESS, accountId })
}

const handleMfaError = error => dispatch => {
  dispatch({
    type: REQUEST_MFA_CODE_FAILURE,
    error: handleError(error)
  })
}

export const requestMfaCode = () => async (dispatch, getState) => {
  try {
    dispatch({
      type: REQUEST_MFA_CODE_REQUEST
    })

    const {
      sourceRequest: { coreApi }
    } = getState()
    const url = `${coreApi}/api/v6/auth/sendMfaCode`
    const { email } = selectMfaUserCreds(getState())

    const response = await request()(url, 'post', encode({ email }))

    dispatch({
      type: REQUEST_MFA_CODE_SUCCESS,
      response
    })
  } catch (err) {
    dispatch(handleMfaError(err))
  }
}

const postCustomer = data => async (dispatch, getState) => {
  const { token, coreApi, accountId } = stateRequest(getState())
  const url = `${coreApi}/api/v6/customers/${accountId}`

  try {
    dispatch({
      type: CUSTOMER_REQUEST
    })

    const customer = await request(token)(url, 'post', encode(data))

    dispatch({
      type: CUSTOMER_SUCCESS,
      data: customer.data.customer
    })
  } catch (err) {
    dispatch(handleCustomerError(err))
  }
}

export const deleteUserCreds = () => ({
  type: DELETE_USER_CREDS
})

export const changePhone = phone => async (dispatch, getState) => {
  const state = getState()
  const unverified = selectUnverifiedPhone(state)

  if (phone === unverified) {
    const message = 'The number you supplied matches the phone number in our records.'
    return dispatch({
      type: CUSTOMER_FORM_ERROR,
      error: {
        message
      }
    })
  }

  dispatch(postCustomer({ phone: phone.replace(/\D/g, '') }))
}

export const clearCustomerError = () => ({
  type: CLEAR_CUSTOMER_ERROR
})

export const handleCustomerError = error => dispatch => {
  dispatch({
    type: CUSTOMER_RESPONSE_ERROR,
    error: handleError(error)
  })
}
